import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import OrderDetailed from '../components/order-detailed/order-detailed';
import OrderDetailedEmpty from '../components/order-detailed/order-detailed-empty';
import { ASSETS_TYPES } from '../constants';
import { useCheckBalanceHook } from '../hooks/checkBalanceHook';
import { useAppDispatch } from '../store';
import { setPaymentIds } from '../store/actions/operations-sign';
import {
  fetchIdOrder,
  fetchRealizationStatuses,
  setFilter,
  setSort,
} from '../store/actions/order-detailed';
import {
  initIssuePayment,
  setSigninIds,
} from '../store/actions/order-realization-detailed';
import { sortByPositions } from '../store/selectors/order-detailed';
import { RootState } from '../types';
import { thunkResultHandler } from '../utils/thunks/thunkResultHandler';

const DetailedOrderContainer: React.FC = () => {
  const filteredItems = useSelector(sortByPositions);
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { getTokenTotalValue } = useCheckBalanceHook();
  const accountInfo = useSelector((state: RootState) => state.auth.account);
  const detailedOrder = useSelector((state: RootState) => state.orderDetailed);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchRealizationStatuses());
  }, []);

  useEffect(() => {
    dispatch(fetchIdOrder({ id: parseInt(params.id, 10) }));
  }, []);

  const orderStatuses = [
    { value: 'ALL', label: 'Все' },
    ...detailedOrder.statuses.map((item) => ({ ...item, value: item.name })),
  ];

  const goDetailedPositions = (
    id: number,
    itemId: number,
    attached?: boolean
  ) => {
    if (attached) {
      history.push(`/orders/${id}/items/${itemId}/realizations?type=attached`);
    } else {
      history.push(`/orders/${id}/items/${itemId}/realizations`);
    }
  };

  const onClose = () => history.push('/orders');

  const onSubmit = (ids: number[], itemsIds: number[]) => {
    dispatch(setSigninIds(ids));
    dispatch(setPaymentIds(itemsIds));
    const routerCb = () =>
      history.push(`/orders/${params.id}/realizations/sign`);

    if (detailedOrder.data.order?.initiatorId === accountInfo.id) {
      history.push(`/orders/${params.id}/selectPayment`, { id: params.id });
    } else {
      dispatch(initIssuePayment({ ids })).then((action) =>
        thunkResultHandler(action, routerCb)
      );
    }
  };

  const getOrder = () => {
    const { order } = detailedOrder.data;
    if (order) {
      if (order.status === 'NEW')
        return (
          <OrderDetailedEmpty
            refId={order.refNumber}
            date={format(new Date(order.openDate), 'dd MMM yyyy', {
              locale: ru,
            })}
            name={order.recipient}
            inn={order.recipientTIN}
            time={format(new Date(order.openDate), 'HH:mm', { locale: ru })}
            status="NEW"
            onClose={onClose}
          />
        );

      return (
        <OrderDetailed
          balance={getTokenTotalValue(ASSETS_TYPES.SIMPLE)}
          onClose={onClose}
          isInitiator={accountInfo.id === detailedOrder.data.order?.initiatorId}
          directions={{
            positions: detailedOrder.sorts.positions.direction,
            count: detailedOrder.sorts.count.direction,
            sum: detailedOrder.sorts.sum.direction,
          }}
          onToggleSort={(name, direction) => {
            dispatch(setSort({ name, direction }));
          }}
          onStatusChange={(val) => {
            dispatch(setFilter({ type: 'status', value: val }));
          }}
          goDetailedPosition={goDetailedPositions}
          onQueryChange={(query) => {
            dispatch(setFilter({ type: 'search', value: query }));
          }}
          statuses={orderStatuses}
          id={parseInt(params.id, 10)}
          onSubmit={onSubmit}
          status={order.status}
          inn={order.recipientTIN}
          name={order.recipient}
          refId={order.refNumber}
          date={format(new Date(order.openDate), 'dd MMM yyyy', { locale: ru })}
          time={format(new Date(order.openDate), 'HH:mm', { locale: ru })}
          data={filteredItems.map((item) => ({
            id: item.id,
            number: item.name,
            positionCount: item.totalCountItem,
            positionAmount: item.positionCount,
            positionSum: item.price,
            issuingInfoId: item.issuingInfoId,
            positionStatus: item.status,
          }))}
        />
      );
    }
    return null;
  };

  return getOrder();
};

export default DetailedOrderContainer;
